import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { TabViewModule } from 'primeng/tabview';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { PostComponent } from '../../components/post/post.component';
import {
  MockProfileService,
  UserProfile,
  Resume,
} from '../../services-mock/mock-profile.service';
import { FeedPost } from '../../services-mock/mock-feed.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    AvatarModule,
    TabViewModule,
    InputTextModule,
    InputTextarea,
    ToastModule,
    PostComponent,
  ],
  providers: [MessageService],
  template: `
    <div class="profile-container">
      <div class="profile-header">
        <div class="profile-avatar-section">
          <p-avatar
            [label]="profile?.initials || 'U'"
            shape="circle"
            size="xlarge"
            styleClass="profile-avatar"
          ></p-avatar>
          <div class="profile-actions">
            <button
              pButton
              type="button"
              [label]="profile?.followersCount + ' Followers'"
              class="follow-button"
              (click)="navigateToFollowers()"
            ></button>
            <button
              pButton
              type="button"
              [label]="profile?.followingCount + ' Following'"
              class="follow-button"
              (click)="navigateToFollowing()"
            ></button>
          </div>
        </div>
        <div class="profile-info">
          <h1 class="profile-name">{{ profile?.name }}</h1>
          <div class="profile-title">
            {{ profile?.title }} at {{ profile?.company }}
          </div>
          <div class="profile-location">
            <i class="pi pi-map-marker"></i>
            {{ profile?.location }}
          </div>
          <div class="profile-bio">{{ profile?.bio }}</div>
        </div>
      </div>

      <div class="profile-content">
        <p-tabView>
          <p-tabPanel header="Posts">
            <div class="posts-section">
              <div class="posts-count" *ngIf="userPosts.length > 0">
                {{ userPosts.length }} post{{
                  userPosts.length !== 1 ? 's' : ''
                }}
              </div>
              <div class="posts-list" *ngIf="userPosts.length > 0">
                <app-post
                  *ngFor="let post of userPosts"
                  [post]="post"
                ></app-post>
              </div>
              <div
                class="no-posts"
                *ngIf="userPosts.length === 0 && !loadingPosts"
              >
                <p>No posts yet. Start sharing your thoughts!</p>
              </div>
            </div>
          </p-tabPanel>

          <p-tabPanel header="Resume">
            <div class="resume-section">
              <div
                class="resume-edit-mode"
                *ngIf="!isEditingResume && isCurrentUserProfile"
              >
                <button
                  pButton
                  type="button"
                  label="Edit Resume"
                  icon="pi pi-pencil"
                  (click)="startEditingResume()"
                  class="edit-button"
                ></button>
              </div>

              <div class="resume-form" *ngIf="isEditingResume && editingResume">
                <div class="form-section">
                  <h3>Summary</h3>
                  <textarea
                    pInputTextarea
                    [(ngModel)]="editingResume!.summary"
                    rows="4"
                    placeholder="Professional summary..."
                    class="summary-input"
                  ></textarea>
                </div>

                <div class="form-section">
                  <h3>Experience</h3>
                  <div
                    *ngFor="let exp of editingResume!.experience; let i = index"
                    class="experience-item"
                  >
                    <div class="form-row">
                      <input
                        type="text"
                        pInputText
                        [(ngModel)]="exp.title"
                        placeholder="Job Title"
                        class="form-input"
                      />
                      <input
                        type="text"
                        pInputText
                        [(ngModel)]="exp.company"
                        placeholder="Company"
                        class="form-input"
                      />
                    </div>
                    <div class="form-row">
                      <input
                        type="text"
                        pInputText
                        [(ngModel)]="exp.location"
                        placeholder="Location"
                        class="form-input"
                      />
                      <input
                        type="date"
                        [value]="formatDateForInput(exp.startDate)"
                        (input)="exp.startDate = parseDateInput($event)"
                        placeholder="Start Date"
                        class="form-input"
                      />
                      <input
                        type="date"
                        [value]="formatDateForInput(exp.endDate)"
                        (input)="exp.endDate = parseDateInput($event)"
                        placeholder="End Date"
                        class="form-input"
                        *ngIf="!exp.current"
                      />
                      <label class="checkbox-label">
                        <input
                          type="checkbox"
                          [(ngModel)]="exp.current"
                          (change)="onCurrentJobChange(exp)"
                        />
                        Current Job
                      </label>
                    </div>
                    <textarea
                      pInputTextarea
                      [(ngModel)]="exp.description"
                      rows="3"
                      placeholder="Job description..."
                      class="description-input"
                    ></textarea>
                    <button
                      pButton
                      type="button"
                      icon="pi pi-trash"
                      label="Remove"
                      (click)="removeExperience(i)"
                      class="remove-button"
                    ></button>
                  </div>
                  <button
                    pButton
                    type="button"
                    icon="pi pi-plus"
                    label="Add Experience"
                    (click)="addExperience()"
                    class="add-button"
                  ></button>
                </div>

                <div class="form-section">
                  <h3>Education</h3>
                  <div
                    *ngFor="let edu of editingResume!.education; let i = index"
                    class="education-item"
                  >
                    <div class="form-row">
                      <input
                        type="text"
                        pInputText
                        [(ngModel)]="edu.degree"
                        placeholder="Degree"
                        class="form-input"
                      />
                      <input
                        type="text"
                        pInputText
                        [(ngModel)]="edu.school"
                        placeholder="School"
                        class="form-input"
                      />
                    </div>
                    <div class="form-row">
                      <input
                        type="text"
                        pInputText
                        [(ngModel)]="edu.location"
                        placeholder="Location"
                        class="form-input"
                      />
                      <input
                        type="date"
                        [value]="formatDateForInput(edu.startDate)"
                        (input)="edu.startDate = parseDateInput($event)"
                        placeholder="Start Date"
                        class="form-input"
                      />
                      <input
                        type="date"
                        [value]="formatDateForInput(edu.endDate)"
                        (input)="edu.endDate = parseDateInput($event)"
                        placeholder="End Date"
                        class="form-input"
                        *ngIf="!edu.current"
                      />
                      <label class="checkbox-label">
                        <input
                          type="checkbox"
                          [(ngModel)]="edu.current"
                          (change)="onCurrentEducationChange(edu)"
                        />
                        Currently Enrolled
                      </label>
                    </div>
                    <textarea
                      pInputTextarea
                      [(ngModel)]="edu.description"
                      rows="2"
                      placeholder="Additional details..."
                      class="description-input"
                    ></textarea>
                    <button
                      pButton
                      type="button"
                      icon="pi pi-trash"
                      label="Remove"
                      (click)="removeEducation(i)"
                      class="remove-button"
                    ></button>
                  </div>
                  <button
                    pButton
                    type="button"
                    icon="pi pi-plus"
                    label="Add Education"
                    (click)="addEducation()"
                    class="add-button"
                  ></button>
                </div>

                <div class="form-section">
                  <h3>Skills</h3>
                  <div class="skills-input">
                    <input
                      type="text"
                      pInputText
                      [(ngModel)]="newSkill"
                      placeholder="Add a skill and press Enter"
                      (keyup.enter)="addSkill()"
                      class="form-input"
                    />
                    <div class="skills-list">
                      <span
                        *ngFor="
                          let skill of editingResume!.skills;
                          let i = index
                        "
                        class="skill-tag"
                      >
                        {{ skill }}
                        <i
                          class="pi pi-times"
                          (click)="removeSkill(i)"
                          style="cursor: pointer; margin-left: 0.5rem;"
                        ></i>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="form-actions">
                  <button
                    pButton
                    type="button"
                    label="Save"
                    icon="pi pi-check"
                    (click)="saveResume()"
                    class="save-button"
                  ></button>
                  <button
                    pButton
                    type="button"
                    label="Cancel"
                    icon="pi pi-times"
                    (click)="cancelEditingResume()"
                    class="cancel-button"
                  ></button>
                </div>
              </div>

              <div class="resume-view" *ngIf="!isEditingResume && profile">
                <div class="resume-section-view">
                  <h3>Summary</h3>
                  <p>{{ profile.resume.summary }}</p>
                </div>

                <div class="resume-section-view">
                  <h3>Experience</h3>
                  <div
                    *ngFor="let exp of profile.resume.experience"
                    class="resume-item"
                  >
                    <div class="resume-item-header">
                      <div>
                        <h4>{{ exp.title }}</h4>
                        <div class="resume-item-meta">
                          {{ exp.company }} • {{ exp.location }}
                        </div>
                      </div>
                      <div class="resume-item-dates">
                        {{ formatDate(exp.startDate) }} -
                        {{ exp.current ? 'Present' : formatDate(exp.endDate!) }}
                      </div>
                    </div>
                    <p>{{ exp.description }}</p>
                  </div>
                </div>

                <div class="resume-section-view">
                  <h3>Education</h3>
                  <div
                    *ngFor="let edu of profile.resume.education"
                    class="resume-item"
                  >
                    <div class="resume-item-header">
                      <div>
                        <h4>{{ edu.degree }}</h4>
                        <div class="resume-item-meta">
                          {{ edu.school }} • {{ edu.location }}
                        </div>
                      </div>
                      <div class="resume-item-dates">
                        {{ formatDate(edu.startDate) }} -
                        {{ edu.current ? 'Present' : formatDate(edu.endDate!) }}
                      </div>
                    </div>
                    <p *ngIf="edu.description">{{ edu.description }}</p>
                  </div>
                </div>

                <div class="resume-section-view">
                  <h3>Skills</h3>
                  <div class="skills-display">
                    <span
                      *ngFor="let skill of profile.resume.skills"
                      class="skill-tag"
                    >
                      {{ skill }}
                    </span>
                  </div>
                </div>

                <div class="resume-section-view">
                  <h3>Certifications</h3>
                  <div
                    *ngFor="let cert of profile.resume.certifications"
                    class="resume-item"
                  >
                    <div class="resume-item-header">
                      <div>
                        <h4>{{ cert.name }}</h4>
                        <div class="resume-item-meta">{{ cert.issuer }}</div>
                      </div>
                      <div class="resume-item-dates">
                        Issued: {{ formatDate(cert.issueDate) }}
                        <span *ngIf="cert.expiryDate">
                          • Expires: {{ formatDate(cert.expiryDate) }}
                        </span>
                      </div>
                    </div>
                    <p *ngIf="cert.credentialId">
                      Credential ID: {{ cert.credentialId }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </p-tabPanel>
        </p-tabView>
      </div>
    </div>
    <p-toast></p-toast>
  `,
  styles: [
    `
      .profile-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 1.5rem;
        background-color: #f5f5f5;
        min-height: calc(100vh - 60px);
      }

      .profile-header {
        background-color: #ffffff;
        border-radius: 8px;
        padding: 2rem;
        margin-bottom: 1.5rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .profile-avatar-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 1.5rem;
      }

      .profile-actions {
        display: flex;
        gap: 1rem;
        margin-top: 1rem;
      }

      .follow-button {
        background: #0066cc;
        color: #ffffff;
        border: none;
      }

      .profile-info {
        text-align: center;
      }

      .profile-name {
        font-size: 2rem;
        font-weight: 600;
        color: #000000;
        margin: 0 0 0.5rem 0;
      }

      .profile-title {
        font-size: 1.1rem;
        color: #666666;
        margin-bottom: 0.5rem;
      }

      .profile-location {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        color: #999999;
        font-size: 0.95rem;
        margin-bottom: 1rem;
      }

      .profile-bio {
        color: #333333;
        line-height: 1.6;
        max-width: 600px;
        margin: 0 auto;
      }

      .profile-content {
        background-color: #ffffff;
        border-radius: 8px;
        padding: 1.5rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .posts-section {
        padding: 1rem 0;
      }

      .posts-count {
        font-size: 1rem;
        color: #666666;
        margin-bottom: 1rem;
      }

      .posts-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .no-posts {
        text-align: center;
        padding: 3rem;
        color: #666666;
      }

      .resume-section {
        padding: 1rem 0;
      }

      .resume-edit-mode {
        margin-bottom: 1.5rem;
      }

      .edit-button {
        background: #0066cc;
        color: #ffffff;
        border: none;
      }

      .form-section {
        margin-bottom: 2rem;
        padding-bottom: 2rem;
        border-bottom: 1px solid #e5e5e5;
      }

      .form-section:last-child {
        border-bottom: none;
      }

      .form-section h3 {
        font-size: 1.25rem;
        font-weight: 600;
        color: #000000;
        margin: 0 0 1rem 0;
      }

      .form-row {
        display: flex;
        gap: 1rem;
        margin-bottom: 1rem;
        flex-wrap: wrap;
      }

      .form-input {
        flex: 1;
        min-width: 200px;
      }

      .checkbox-label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
        color: #333333;
      }

      .summary-input,
      .description-input {
        width: 100%;
        margin-bottom: 1rem;
      }

      .experience-item,
      .education-item {
        background-color: #f9f9f9;
        padding: 1.5rem;
        border-radius: 8px;
        margin-bottom: 1rem;
      }

      .remove-button {
        background: #dc3545;
        color: #ffffff;
        border: none;
        margin-top: 0.5rem;
      }

      .add-button {
        background: #28a745;
        color: #ffffff;
        border: none;
      }

      .skills-input {
        margin-bottom: 1rem;
      }

      .skills-list,
      .skills-display {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 1rem;
      }

      .skill-tag {
        background-color: #e3f2fd;
        color: #0066cc;
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-size: 0.9rem;
        font-weight: 500;
        display: inline-flex;
        align-items: center;
      }

      .form-actions {
        display: flex;
        gap: 1rem;
        margin-top: 2rem;
      }

      .save-button {
        background: #0066cc;
        color: #ffffff;
        border: none;
      }

      .cancel-button {
        background: transparent;
        color: #666666;
        border: 1px solid #e5e5e5;
      }

      .resume-section-view {
        margin-bottom: 2rem;
        padding-bottom: 2rem;
        border-bottom: 1px solid #e5e5e5;
      }

      .resume-section-view:last-child {
        border-bottom: none;
      }

      .resume-section-view h3 {
        font-size: 1.5rem;
        font-weight: 600;
        color: #000000;
        margin: 0 0 1.5rem 0;
      }

      .resume-item {
        margin-bottom: 1.5rem;
      }

      .resume-item-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 0.5rem;
        flex-wrap: wrap;
        gap: 1rem;
      }

      .resume-item-header h4 {
        font-size: 1.1rem;
        font-weight: 600;
        color: #000000;
        margin: 0 0 0.25rem 0;
      }

      .resume-item-meta {
        color: #666666;
        font-size: 0.95rem;
      }

      .resume-item-dates {
        color: #999999;
        font-size: 0.9rem;
        white-space: nowrap;
      }

      .resume-item p {
        color: #333333;
        line-height: 1.6;
        margin: 0.5rem 0 0 0;
      }

      .loading-posts {
        text-align: center;
        padding: 2rem;
        color: #666666;
      }

      :host ::ng-deep {
        .profile-avatar {
          width: 120px;
          height: 120px;
          font-size: 3rem;
        }

        .p-tabview-nav {
          background: transparent;
        }

        .p-tabview-nav-link {
          padding: 1rem 1.5rem;
        }
      }
    `,
  ],
})
export class ProfileComponent implements OnInit {
  profile: UserProfile | null = null;
  userPosts: FeedPost[] = [];
  loadingPosts = false;
  isEditingResume = false;
  editingResume: Resume | null = null;
  newSkill = '';
  userId: string | null = null;
  isCurrentUserProfile = false;

  constructor(
    private profileService: MockProfileService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.userId = params.get('userId');
      this.checkIfCurrentUser();
      this.loadProfile();
      this.loadUserPosts();
    });
  }

  checkIfCurrentUser() {
    const currentProfile = this.authService.getCurrentProfile();
    if (!currentProfile) {
      this.isCurrentUserProfile = false;
      return;
    }

    const currentUserName = `${currentProfile.firstName || ''} ${
      currentProfile.lastName || ''
    }`.trim();
    // If no userId in route, it's the current user's profile
    this.isCurrentUserProfile = !this.userId || this.userId === 'current-user';
  }

  loadProfile() {
    this.profileService
      .getProfile(this.userId || undefined)
      .subscribe((profile) => {
        this.profile = profile;
        this.checkIfCurrentUser();
      });
  }

  loadUserPosts() {
    this.loadingPosts = true;
    this.profileService
      .getUserPosts(this.userId || undefined)
      .subscribe((posts) => {
        this.userPosts = posts;
        this.loadingPosts = false;
      });
  }

  navigateToFollowers() {
    this.router.navigate(['/profile/followers']);
  }

  navigateToFollowing() {
    this.router.navigate(['/profile/following']);
  }

  startEditingResume() {
    if (this.profile) {
      this.editingResume = JSON.parse(JSON.stringify(this.profile.resume));
      this.isEditingResume = true;
    }
  }

  cancelEditingResume() {
    this.isEditingResume = false;
    this.editingResume = null;
    this.newSkill = '';
  }

  saveResume() {
    if (this.editingResume && this.profile) {
      this.profileService
        .updateResume(this.editingResume)
        .subscribe((resume) => {
          if (this.profile) {
            this.profile.resume = resume;
          }
          this.isEditingResume = false;
          this.editingResume = null;
          this.newSkill = '';
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Resume updated successfully',
          });
        });
    }
  }

  addExperience() {
    if (this.editingResume) {
      this.editingResume.experience.push({
        id: 'exp-' + Date.now(),
        title: '',
        company: '',
        location: '',
        startDate: new Date(),
        current: false,
        description: '',
      });
    }
  }

  removeExperience(index: number) {
    if (this.editingResume) {
      this.editingResume.experience.splice(index, 1);
    }
  }

  addEducation() {
    if (this.editingResume) {
      this.editingResume.education.push({
        id: 'edu-' + Date.now(),
        degree: '',
        school: '',
        location: '',
        startDate: new Date(),
        current: false,
      });
    }
  }

  removeEducation(index: number) {
    if (this.editingResume) {
      this.editingResume.education.splice(index, 1);
    }
  }

  addSkill() {
    if (this.newSkill.trim() && this.editingResume) {
      this.editingResume.skills.push(this.newSkill.trim());
      this.newSkill = '';
    }
  }

  removeSkill(index: number) {
    if (this.editingResume) {
      this.editingResume.skills.splice(index, 1);
    }
  }

  onCurrentJobChange(exp: any) {
    if (exp.current) {
      exp.endDate = undefined;
    }
  }

  onCurrentEducationChange(edu: any) {
    if (edu.current) {
      edu.endDate = undefined;
    }
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  }

  formatDateForInput(date: Date | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  parseDateInput(event: any): Date {
    const value = event.target.value;
    if (!value) return new Date();
    return new Date(value);
  }
}
